import { cantidadDe, formatoCantidad, formatoFechaBolivia } from '@/lib/utils/formato-precio';
import type { EntradaDelHistorial, UltimoCambio } from '@/types/historial.types';
import { permisoEnPalabras } from '../components/permiso-de-estrategia';
import { NOMBRE_CADENCIA } from '../components/nueva-estrategia/paso-3-cuando';
import type { Cadence, StrategyModeLeido } from '../types/strategy-applications.types';

/**
 * Spec 294 (D-R4-5): el historial de cambios, en palabras. Lo de quién lo hizo
 * (`sujeto`, `quienLoHizo`, `lineaDeQuien`) lo usan también las reglas.
 * Nunca un nombre de campo interno: un campo que no se conoce no se nombra.
 */

export interface EntradaEnPalabras {
  titulo: string;
  lineas: string[];
}

type Foto = Record<string, unknown>;

const esObjeto = (v: unknown): v is Foto => typeof v === 'object' && v !== null;

/** El detalle de la entrada, como objeto (vacío si no hay). */
export const detalleDe = (e: EntradaDelHistorial): Foto => (esObjeto(e.detalle) ? e.detalle : {});

/** Una creación que anotó la migración (ya existía antes del registro). */
export const esDelRelleno = (e: EntradaDelHistorial): boolean => detalleDe(e).relleno === true;

/** «Ana López», «Sole Soporte, del soporte de Prixia, viendo como Ana López», «El sistema». */
export function quienLoHizo(e: EntradaDelHistorial): string {
  if (e.esElSistema) return 'El sistema';
  if (!e.quien) return 'Alguien que ya no está en la empresa';
  const partes = [e.quien];
  if (e.quienEsSoporte) partes.push('del soporte de Prixia');
  if (e.actuandoComo) partes.push(`viendo como ${e.actuandoComo}`);
  return partes.join(', ');
}

/** Quién, para empezar una frase: con la coma de cierre si lleva aclaraciones. */
export function sujeto(e: EntradaDelHistorial): string {
  const q = quienLoHizo(e);
  return e.quien && !e.esElSistema && (e.quienEsSoporte || e.actuandoComo) ? `${q},` : q;
}

/** «Ya existía…»: la creación del relleno sin autor. null si no es ese caso. */
export function creacionSinAutor(e: EntradaDelHistorial): EntradaEnPalabras | null {
  if (e.accion !== 'CREATED' || !esDelRelleno(e) || e.quien) return null;
  return {
    titulo: 'No se sabe quién la creó',
    lineas: ['Ya existía cuando empezó el registro de cambios.'],
  };
}

/** «el permiso y la fecha de fin» (para el título de una edición de varios campos). */
export function enumerar(xs: string[]): string {
  if (xs.length === 0) return 'algo';
  if (xs.length === 1) return xs[0];
  return `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;
}

const conMayuscula = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** Un valor en medio de una frase («Antes: todos los días…»): con minúscula inicial. */
export const enMinuscula = (s: string | null): string | null =>
  s == null ? null : s.charAt(0).toLowerCase() + s.slice(1);

/** Un campo editado, en palabras: su nombre y, si se pueden decir, los valores. */
export interface CampoEditado {
  campo: string;
  antes: string | null;
  despues: string | null;
}

/**
 * El título y las líneas de una edición: el título nombra lo que cambió y los
 * valores van debajo, «Antes: A · Ahora: B» (ronda final de R4: «de A a B»
 * chocaba con «a las»). Con varios campos, una línea por campo. Un campo sin
 * valores que decir, o que se dice igual antes y después, se nombra solo: nunca
 * «de X a X».
 */
export function edicionEnPalabras(
  quien: string,
  campos: CampoEditado[],
  sufijo = ''
): EntradaEnPalabras {
  const conValores = (c: CampoEditado) =>
    c.antes != null && c.despues != null && c.antes !== c.despues;
  const antesAhora = (c: CampoEditado) => `Antes: ${c.antes} · Ahora: ${c.despues}`;
  if (campos.length === 1) {
    const c = campos[0];
    return {
      titulo: `${quien} cambió ${c.campo}${sufijo}`,
      lineas: conValores(c) ? [antesAhora(c)] : [],
    };
  }
  return {
    titulo: `${quien} cambió ${enumerar(campos.map((c) => c.campo))}${sufijo}`,
    lineas: campos.filter(conValores).map((c) => `${conMayuscula(c.campo)} · ${antesAhora(c)}`),
  };
}

/** La fecha de fin: «sin fecha de fin» o el día en Bolivia. */
export function fechaDeFin(v: unknown): string | null {
  if (v === null) return 'sin fecha de fin';
  return typeof v === 'string' ? formatoFechaBolivia(v) : null;
}

/** Un producto de un detalle: «Televisor 55"» (TV-55), o el código solo. */
interface ProductoDelDetalle {
  sku: string;
  nombre: string | null;
}
/** « · se retiraron 37 de Por decidir» (nada si no retiró ninguna). */
function retiradasEnFrase(d: Foto): string {
  const n = typeof d.retiradas === 'number' ? d.retiradas : 0;
  return n > 0
    ? ` · ${n === 1 ? 'se retiró' : 'se retiraron'} ${formatoCantidad(n)} de Por decidir`
    : '';
}

const productosDe = (d: Foto): ProductoDelDetalle[] =>
  Array.isArray(d.productos)
    ? d.productos.filter(
        (p): p is ProductoDelDetalle => esObjeto(p) && typeof (p as Foto).sku === 'string'
      )
    : [];
const productoEnPalabras = (p: ProductoDelDetalle): string =>
  p.nombre ? `«${p.nombre}» (${p.sku})` : p.sku;
/** Los productos de una entrada, uno por línea, hasta 10. */
const MAX_PRODUCTOS = 10;
function lineasDeProductos(ps: ProductoDelDetalle[]): string[] {
  if (ps.length <= 1) return [];
  const lineas = ps.slice(0, MAX_PRODUCTOS).map(productoEnPalabras);
  if (ps.length > MAX_PRODUCTOS) lineas.push(`y ${formatoCantidad(ps.length - MAX_PRODUCTOS)} más`);
  return lineas;
}
const nombreDeLaOtra = (d: Foto): string => {
  const por = esObjeto(d.por) ? d.por : {};
  return typeof por.nombre === 'string' && por.nombre ? `«${por.nombre}»` : 'otra estrategia';
};

// ── La cadencia (la misma frase que la tarjeta) ──────────────────────────────

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export interface Cadencia {
  cadence: Cadence;
  cadenceHour: number | null;
  cadenceMinute: number;
  cadenceDayOfWeek: number | null;
}

/** «Todos los días a las 06:00», «Miércoles a las 09:30»… (la frase de la tarjeta). */
export function cadaCuantoRevisa(e: Cadencia): string {
  if (e.cadence === 'ON_DEMAND') return NOMBRE_CADENCIA.ON_DEMAND;
  if (e.cadence === 'INSTANT') return NOMBRE_CADENCIA.INSTANT;
  const hora = `${String(e.cadenceHour ?? 0).padStart(2, '0')}:${String(e.cadenceMinute).padStart(2, '0')}`;
  if (e.cadence === 'DAILY') return `Todos los días a las ${hora}`;
  return `${DIAS[e.cadenceDayOfWeek ?? 0]} a las ${hora}`;
}

const CAMPOS_DE_CADENCIA = ['cadence', 'cadenceHour', 'cadenceMinute', 'cadenceDayOfWeek'];
const CAMPOS_DEL_PERMISO = ['mode', 'askAboveChangePct'];
/** Los campos que se editan de una estrategia (los de la foto del servidor). */
const CAMPOS_DE_LA_FOTO = [...CAMPOS_DEL_PERMISO, ...CAMPOS_DE_CADENCIA, 'validUntil'];

/** La cadencia de una foto, si tiene lo que hace falta para decirla. */
function cadenciaDe(f: Foto): string | null {
  const c = f.cadence;
  if (c === 'ON_DEMAND' || c === 'INSTANT') return cadaCuantoRevisa(f as unknown as Cadencia);
  if (c !== 'DAILY' && c !== 'WEEKLY') return null;
  if (typeof f.cadenceHour !== 'number' || typeof f.cadenceMinute !== 'number') return null;
  if (c === 'WEEKLY' && typeof f.cadenceDayOfWeek !== 'number') return null;
  return cadaCuantoRevisa(f as unknown as Cadencia);
}

/** El permiso de una foto, si tiene lo que hace falta para decirlo. */
function permisoDe(f: Foto): string | null {
  if (typeof f.mode !== 'string' || !('askAboveChangePct' in f)) return null;
  const umbral = typeof f.askAboveChangePct === 'number' ? f.askAboveChangePct : null;
  return permisoEnPalabras(f.mode as StrategyModeLeido, umbral);
}

/**
 * Una edición guarda sólo lo que cambió («cambió la hora» no trae el día). Para
 * decir la frase entera hace falta la foto completa de antes y de después: se
 * arma desde la estrategia como está hoy (`actual`) hacia atrás, entrada por
 * entrada, con lo más nuevo primero (el orden en que llegan). Lo que no es una
 * edición no cambia estos campos.
 */
export function fotosCompletas(
  entradas: EntradaDelHistorial[],
  actual: Foto | null
): Map<string, { antes: Foto; despues: Foto }> {
  const out = new Map<string, { antes: Foto; despues: Foto }>();
  if (!actual) return out;
  let estado: Foto = {};
  for (const c of CAMPOS_DE_LA_FOTO) if (c in actual) estado[c] = actual[c];
  for (const e of entradas) {
    if (e.accion !== 'EDITED') continue;
    const despues = { ...estado, ...(esObjeto(e.despues) ? e.despues : {}) };
    const antes = { ...despues, ...(esObjeto(e.antes) ? e.antes : {}) };
    out.set(e.id, { antes, despues });
    estado = antes;
  }
  return out;
}

/** La foto de una estrategia como está hoy (el punto de partida de `fotosCompletas`). */
export function fotoDeHoy(a: {
  mode: string;
  askAboveChangePct?: number | null;
  cadence: string;
  cadenceHour: number | null;
  cadenceMinute: number;
  cadenceDayOfWeek: number | null;
  validUntil?: string | null;
}): Foto {
  return {
    mode: a.mode,
    askAboveChangePct: a.askAboveChangePct ?? null,
    cadence: a.cadence,
    cadenceHour: a.cadenceHour,
    cadenceMinute: a.cadenceMinute,
    cadenceDayOfWeek: a.cadenceDayOfWeek,
    validUntil: a.validUntil ?? null,
  };
}

/** Los campos de una edición de estrategia, en palabras y en el orden de la ficha. */
function camposEditados(
  e: EntradaDelHistorial,
  foto?: { antes: Foto; despues: Foto }
): CampoEditado[] {
  const antes = esObjeto(e.antes) ? e.antes : {};
  const despues = esObjeto(e.despues) ? e.despues : {};
  const cambio = (cs: string[]) => cs.some((c) => c in despues || c in antes);
  // Con la foto completa, lo que falta sale de ahí; sin ella, sólo lo que trae la entrada.
  const a = foto ? { ...foto.antes, ...antes } : antes;
  const d = foto ? { ...foto.despues, ...despues } : despues;
  const out: CampoEditado[] = [];
  if (cambio(CAMPOS_DEL_PERMISO))
    out.push({
      campo: 'el permiso',
      antes: enMinuscula(permisoDe(a)),
      despues: enMinuscula(permisoDe(d)),
    });
  if (cambio(CAMPOS_DE_CADENCIA))
    out.push({
      campo: 'cada cuánto revisa',
      antes: enMinuscula(cadenciaDe(a)),
      despues: enMinuscula(cadenciaDe(d)),
    });
  if (cambio(['validUntil']))
    out.push({
      campo: 'la fecha de fin',
      antes: 'validUntil' in a ? fechaDeFin(a.validUntil) : null,
      despues: 'validUntil' in d ? fechaDeFin(d.validUntil) : null,
    });
  return out;
}

/**
 * Una entrada del historial de una estrategia, en palabras. `foto` (de
 * `fotosCompletas`) completa lo que una edición no guardó.
 */
export function historialDeEstrategiaEnPalabras(
  e: EntradaDelHistorial,
  foto?: { antes: Foto; despues: Foto }
): EntradaEnPalabras {
  const quien = sujeto(e);
  const d = detalleDe(e);
  switch (e.accion) {
    case 'CREATED':
      return creacionSinAutor(e) ?? { titulo: `${quien} la creó`, lineas: [] };
    case 'EDITED': {
      const campos = camposEditados(e, foto);
      return campos.length > 0
        ? edicionEnPalabras(quien, campos)
        : { titulo: `${quien} cambió algo`, lineas: [] };
    }
    case 'TURNED_ON':
      return { titulo: `${quien} la encendió`, lineas: [] };
    case 'TURNED_OFF':
      return { titulo: `${quien} la apagó${retiradasEnFrase(d)}`, lineas: [] };
    // Ronda del QA de R4: la baja también retira lo que esperaba (`detalle.retiradas`).
    case 'DELETED':
      return { titulo: `${quien} la dio de baja${retiradasEnFrase(d)}`, lineas: [] };
    case 'PRODUCTS_REMOVED': {
      const ps = productosDe(d);
      return {
        titulo:
          ps.length === 1
            ? `${quien} le sacó ${productoEnPalabras(ps[0])}`
            : `${quien} le sacó ${cantidadDe(ps.length, 'producto', 'productos')}`,
        lineas: lineasDeProductos(ps),
      };
    }
    case 'PRODUCTS_TAKEN': {
      const ps = productosDe(d);
      return {
        titulo: `${conMayuscula(nombreDeLaOtra(d))} le tomó ${cantidadDe(ps.length, 'producto', 'productos')} (lo hizo ${quienLoHizo(e)})`,
        lineas: lineasDeProductos(ps),
      };
    }
    case 'PRODUCTS_RETURNED': {
      const ps = productosDe(d);
      const verbo = ps.length === 1 ? 'Volvió' : 'Volvieron';
      const quienFue = e.esElSistema ? '' : ` (lo hizo ${quienLoHizo(e)})`;
      return {
        titulo: `${verbo} ${cantidadDe(ps.length, 'producto', 'productos')} que tenía ${nombreDeLaOtra(d)}${quienFue}`,
        lineas: lineasDeProductos(ps),
      };
    }
    case 'CLOSED_EXPIRED':
      return {
        titulo:
          e.esElSistema || !e.quien
            ? 'Venció: la cerró el sistema'
            : `Venció: la cerró ${quienLoHizo(e)}`,
        lineas: [],
      };
    default:
      return { titulo: `${quien} cambió algo`, lineas: [] };
  }
}

/**
 * «Creada por Ana López · Último cambio por Roberto Méndez el 6 oct 2026» (la
 * tarjeta y la fila). Lo que no se sabe no se dice; sin nada, null.
 */
export function lineaDeQuien(x: {
  creadaPor?: string | null;
  creadaPorSoporte?: boolean;
  ultimoCambio?: UltimoCambio | null;
}): string | null {
  const partes: string[] = [];
  if (x.creadaPor)
    partes.push(`Creada por ${x.creadaPor}${x.creadaPorSoporte ? ', del soporte de Prixia' : ''}`);
  const u = x.ultimoCambio;
  if (u) {
    const fecha = formatoFechaBolivia(u.cuando);
    if (u.esElSistema) partes.push(`Último cambio: el sistema, el ${fecha}`);
    else if (!u.quien)
      partes.push(`Último cambio por alguien que ya no está en la empresa el ${fecha}`);
    else if (u.quienEsSoporte)
      partes.push(`Último cambio por ${u.quien}, del soporte de Prixia, el ${fecha}`);
    else partes.push(`Último cambio por ${u.quien} el ${fecha}`);
  }
  return partes.length > 0 ? partes.join(' · ') : null;
}
